import axios from 'axios';
import router from '@/router';
import { getCookies, setCookies } from '@/utils/utils';
import { message, notification } from 'ant-design-vue';

// 创建一个axios实例
const service = axios.create({
  baseURL: process.env.VUE_APP_BASE_URL,
  timeout: 60000 // 请求超时时间
});

// 后端服务地址
const serviceMap = {
  'data-service': process.env.VUE_APP_DADA_URL,
  'user-service': process.env.VUE_APP_USER_URL,
  'devops-services': process.env.VUE_APP_SERVICES_URL,
  'data-center': process.env.VUE_APP_DATACENTER_URL,
  'track-service': process.env.VUE_APP_TRACK_URL,
  datacenter: process.env.VUE_APP_DATACENTER_URL,
  nvwa: process.env.VUE_APP_NVWA_URL
};

// 请求拦截器
service.interceptors.request.use(
  config => {
    config.baseURL = serviceMap[config.serviceName] || process.env.VUE_APP_DADA_URL;
    const token = getCookies('token');
    config.headers.Authorization = token;
    config.headers.ContentType = 'application/json;charset=UTF-8';
    return config;
  },
  error => {
    console.log(error); // for debug
    return Promise.reject(error);
  }
);

service.interceptors.response.use(
  response => {
    /**
     * 当code为4005,且当前页面不为/login时，跳转至登录页面
     * 并携带当前页面地址，登录成功后重新跳转至当前页面
     */
    if (!router) return response.data;
    switch (response.data.code) {
      case 4002:
        if (!['/', '/login'].includes(router.currentRoute.path)) {
          return refreshTokenHandler(response.config);
        }
        break;
      case 401:
        return refreshTokenHandler(response.config);
      case 2004:
        return Promise.reject(response.data);
    }
    if (response.headers['content-type'].indexOf('application/json')) {
      return response;
    }

    if (![2000, 200].includes(response.data.code) && response.config.url !== '/v1/case/import' && response.config.url !== '/v1/case/check') {
      message.warning(response.data.message);
      return Promise.reject(response.data);
    }
    return response.data;
  },
  function handerError(error) {
    switch (error.response.status) {
      case 500:
        if (error.response.config.url === '/api/auth/getUserSignature') {
          break;
        }
        notification.error({
          message: '网络连接失败',
          description: '网络连接失败，请检查网络后再试'
        });
        break;
      case 401:
        return refreshTokenHandler(error.response.config);
      case 404:
        message.warning('请求地址不存在');
    }
    return Promise.reject(error);
  }
);

export function get(url, params = {}) {
  return service({
    method: 'get',
    url,
    params
  });
}

export function post(url, data = {}, params = {}) {
  return service({
    method: 'post',
    url,
    data,
    params
  });
}

export function _delete(url, params = {}) {
  return service({
    method: 'delete',
    url,
    params
  });
}

export function formPost(url, data = {}, params = {}) {
  return service({
    headers: {
      'Content-Type': 'application/x-www-form-urlencoded;charset=UTF-8'
    },
    data,
    transformRequest: [
      data => {
        let res = '';
        for (const k in data) {
          res += encodeURIComponent(k) + '=' + encodeURIComponent(data[k]) + '&';
        }
        return res;
      }
    ],
    method: 'post',
    url,
    params
  });
}

let isRefreshing = false; // 是否正在刷新的标记
let requestList = []; // 重试队列,为了解决同时发起两个或两个以上的请求时，过期如何处理

export function refreshTokenHandler(config) {
  if (!isRefreshing) {
    // 正在刷新，执行else里面的逻辑
    isRefreshing = true;
    const refreshToken = getCookies('refreshToken');
    if (!refreshToken || refreshToken === undefined) {
      message.warning('登录状态失效，请重新登陆');
      window.location.href = process.env.VUE_APP_SSO_URL + '?url=' + window.location.href;
    }
    return axios
      .post(process.env.VUE_APP_BASE_URL + '/v1/auth/refreshToken', {
        refreshToken: refreshToken
      })
      .then(res => {
        const resData = res.data;
        if (resData.code !== 2000) {
          message.warning('用户登录凭据已失效，请重新登陆');
          setCookies('token'); // 清除token
          setCookies('refreshToken'); // 清除refreshToken
          window.location.href = process.env.VUE_APP_SSO_URL + '?url=' + window.location.href;
        } else {
          config.headers['Authorization'] = resData.data.token;
          setCookies('token', resData.data.token, 30);
          setCookies('refreshToken', resData.data.refreshToken, 30);
          requestList.forEach(cb => {
            return cb(resData.data.token);
          });
          requestList = [];
          return service(config);
        }
      })
      .catch(() => {
        message.warning('用户登录凭据已失效，请重新登陆');
        window.location.href = process.env.VUE_APP_SSO_URL + '?url=' + window.location.href;
      })
      .finally(() => {
        isRefreshing = false;
      });
  } else {
    return new Promise(resolve => {
      // 将resolve放进队列，用一个函数形式来保存，等token刷新后直接执行
      requestList.push(token => {
        config.headers['Authorization'] = token;
        resolve(service(config));
      });
    });
  }
}

export default service;
